import React, { useEffect, useState } from "react";
import { Dialog } from "components/main/utils/Dialog";
import { Button } from "components/common/Button";
import { TextInput } from "components/common/TextInput";
import { Select } from "components/common/Select";
import { FormField } from "components/common/FormField";
import { Callout } from "components/common/Callout";
import { InlineSpinner } from "components/common/InlineSpinner";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import { SectionRule } from "components/common/SectionRule";
import { Tooltip } from "components/common/Tooltip";
import { TooltipButton } from "components/common/TooltipButton";
import { Collapsible } from "components/common/Collapsible";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import { countryOptions } from "@/lib/countries";
import { useAddressCheck } from "components/main/organizations/useAddressCheck";
import type {
  BillingAddress,
  BillingBusiness,
  BillingDetails,
  BillingDetailsUpdate,
  ContactPreviewOutcome,
} from "@/lib/api/organizations";

/// The email rides its own door (`PUT /billing/contact`); the rest is one write.
export interface BillingDetailsSave {
  email: string | null;
  details: BillingDetailsUpdate;
}

const COUNTRIES = countryOptions();

interface Draft {
  email: string;
  name: string;
  first_line: string;
  second_line: string;
  city: string;
  region: string;
  postal_code: string;
  country_code: string;
  company: string;
  tax_identifier: string;
  company_number: string;
  /// One recipient per line, `Name <email>` or a bare address.
  recipients: string;
}

const blank = (email: string): Draft => ({
  email,
  name: "",
  first_line: "",
  second_line: "",
  city: "",
  region: "",
  postal_code: "",
  country_code: "",
  company: "",
  tax_identifier: "",
  company_number: "",
  recipients: "",
});

const fromDetails = (email: string, d: BillingDetails): Draft => ({
  email,
  name: d.name ?? "",
  first_line: d.address?.first_line ?? "",
  second_line: d.address?.second_line ?? "",
  city: d.address?.city ?? "",
  region: d.address?.region ?? "",
  postal_code: d.address?.postal_code ?? "",
  country_code: d.address?.country_code ?? "",
  company: d.business?.name ?? "",
  tax_identifier: d.business?.tax_identifier ?? "",
  company_number: d.business?.company_number ?? "",
  recipients: (d.business?.contacts ?? [])
    .map((c) => (c.name === c.email ? c.email : `${c.name} <${c.email}>`))
    .join("\n"),
});

const orNull = (s: string) => (s.trim() === "" ? null : s.trim());

/// `Name <email>` or a bare address, which is its own name (Paddle wants one).
function parseRecipients(text: string): { name: string; email: string }[] {
  return text
    .split(/\n|,/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const m = /^(.*?)\s*<([^>]+)>$/.exec(line);
      return m
        ? { name: m[1].trim() || m[2].trim(), email: m[2].trim() }
        : { name: line, email: line };
    });
}

export const BillingDetailsDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  email: string;
  /// The card the last charge was taken on; a label for `cardOnFile`, which
  /// stays the answer when this is `null`.
  card: {
    brand: string | null;
    last4: string | null;
    expires: string | null;
  } | null;
  /// `null` until a confirmed payment named someone.
  paidBy: { name: string; email: string; is_you: boolean } | null;
  /// `null` when the processor could not be asked — never rendered as "no card".
  cardOnFile: boolean | null;
  onUpdateCard: (() => void) | null;
  /// The day a scheduled cancellation ends the subscription; non-null locks the
  /// form, since no invoice is coming to carry the paperwork.
  endsOn: string | null;
  /// The input the processor's refusal named, so the message lands on it.
  errorField: string | null;
  onOpenPortal: (() => void) | null;
  load: () => Promise<BillingDetails>;
  previewEmail: (email: string) => Promise<ContactPreviewOutcome | null>;
  busy: boolean;
  error: string | null;
  onSave: (save: BillingDetailsSave) => void;
}> = ({
  open,
  onClose,
  email,
  card,
  paidBy,
  cardOnFile,
  onUpdateCard,
  endsOn,
  errorField,
  onOpenPortal,
  load,
  previewEmail,
  busy,
  error,
  onSave,
}) => {
  const [details, setDetails] = useState<BillingDetails | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(blank(email));

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setDetails(null);
    setLoadError(null);
    setDraft(blank(email));
    load()
      .then((d) => {
        if (cancelled) return;
        setDetails(d);
        setDraft(fromDetails(email, d));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(
          err instanceof Error
            ? err.message
            : "Billing details could not be read.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [open, email, load]);

  const set = (key: keyof Draft) => (value: string) =>
    setDraft((d) => ({ ...d, [key]: value }));
  /// A taken address can still be a recipient: moves it there and restores the
  /// billing email.
  const addTypedAddressAsRecipient = () =>
    setDraft((d) => {
      const address = d.email.trim();
      const lines = d.recipients
        .split(/\n|,/)
        .map((l) => l.trim())
        .filter(Boolean);
      const already = lines.some((l) => l.endsWith(address) || l === address);
      return {
        ...d,
        email,
        recipients: already ? d.recipients : [...lines, address].join("\n"),
      };
    });

  const editable = (details?.editable ?? false) && endsOn === null;
  /// One `id` for the label's `htmlFor` and its control, so they cannot drift.
  const field = (
    id: string,
    label: string,
    key: keyof Draft,
    extra?: {
      info?: string;
      helper?: string;
      error?: string;
      placeholder?: string;
      type?: "email";
      multiline?: boolean;
      disabled?: boolean;
    },
  ) => (
    <FormField
      label={label}
      htmlFor={`billing-${id}`}
      info={extra?.info}
      helper={extra?.helper}
      error={extra?.error}
    >
      <TextInput
        id={`billing-${id}`}
        value={draft[key]}
        onChange={set(key)}
        type={extra?.type}
        placeholder={extra?.placeholder}
        multiline={extra?.multiline}
        minRows={extra?.multiline ? 2 : undefined}
        disabled={extra?.disabled ?? !editable}
      />
    </FormField>
  );
  /// A refusal naming an unknown field stays the dialog-level message.
  const CLAIMABLE = ["tax_identifier", "postal_code"];
  const claimedByField =
    error !== null && errorField !== null && CLAIMABLE.includes(errorField);
  const fieldError = (name: string) =>
    claimedByField && errorField === name ? error : undefined;
  const emailEdited = draft.email.trim() !== email && draft.email.trim() !== "";
  const { verdict: outcome } = useAddressCheck(
    draft.email.trim(),
    emailEdited,
    previewEmail,
  );

  /// The door refuses a taken address, so Save is blocked before the press.
  const addressTaken = emailEdited && outcome === "taken";
  const emailHint = addressTaken
    ? null
    : outcome === "unknown"
      ? "We couldn't check this email. If it is already used for billing elsewhere the save will be refused, and nothing changes."
      : outcome === "rename"
        ? "Invoices and receipts will go to this address instead. The card and company details stay as they are."
        : null;
  const cardName = ((): string => {
    if (!cardOnFile) return "No card on file";
    if (!card) return "Card on file";
    const scheme = card.brand
      ? card.brand.charAt(0).toUpperCase() + card.brand.slice(1)
      : "Card";
    return card.last4 ? `${scheme} ending ${card.last4}` : scheme;
  })();

  /// `null` until asked; unasked, the section follows what the processor holds.
  /// Not gated on the org being a company: a sole trader has a tax number too.
  const [companyAsked, setCompanyAsked] = useState<boolean | null>(null);
  const hasBusiness = Boolean(details?.business);
  /// Checkout leaves a bare country on every subscription; only a street or a
  /// city means somebody entered an address.
  const hasAddress = Boolean(
    details?.address?.first_line || details?.address?.city,
  );
  const wantsBusiness =
    draft.company.trim() !== "" ||
    draft.tax_identifier.trim() !== "" ||
    draft.company_number.trim() !== "" ||
    draft.recipients.trim() !== "";
  const companyMissing = wantsBusiness && draft.company.trim() === "";
  /// A section whose error disables Save cannot be folded away.
  const showCompany = (companyAsked ?? hasBusiness) || companyMissing;
  const [addressAsked, setAddressAsked] = useState(false);
  const wantsAddress =
    draft.first_line.trim() !== "" ||
    draft.city.trim() !== "" ||
    draft.postal_code.trim() !== "" ||
    draft.country_code !== "";
  /// The processor takes an address only with a country.
  const countryMissing = wantsAddress && draft.country_code === "";
  const showAddress =
    addressAsked || countryMissing || fieldError("postal_code") !== undefined;
  const countryChanged =
    details?.address != null &&
    draft.country_code !== "" &&
    draft.country_code !== details.address.country_code;

  const submit = () => {
    if (!details) return;
    const update: BillingDetailsUpdate = {};
    if (draft.name.trim() !== (details.name ?? ""))
      update.name = draft.name.trim();
    if (details.address || wantsAddress) {
      const address: BillingAddress = {
        first_line: orNull(draft.first_line),
        second_line: orNull(draft.second_line),
        city: orNull(draft.city),
        region: orNull(draft.region),
        postal_code: orNull(draft.postal_code),
        country_code: draft.country_code,
      };
      if (JSON.stringify(address) !== JSON.stringify(details.address)) {
        update.address = address;
      }
    }
    if (wantsBusiness) {
      const business: BillingBusiness = {
        name: draft.company.trim(),
        company_number: orNull(draft.company_number),
        tax_identifier: orNull(draft.tax_identifier),
        contacts: parseRecipients(draft.recipients),
      };
      if (JSON.stringify(business) !== JSON.stringify(details.business)) {
        update.business = business;
      }
    }
    const emailChanged = draft.email.trim() !== email;
    onSave({
      email: emailChanged ? draft.email.trim() : null,
      details: update,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Billing details"
      size="medium"
      overlayScrollbar
      actions={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            loading={busy}
            disabled={
              !details || companyMissing || countryMissing || endsOn !== null
            }
            onClick={submit}
            data-testid="save-billing-details"
          >
            Save
          </Button>
        </>
      }
    >
      {loadError ? (
        <span className="text-error">{loadError}</span>
      ) : !details ? (
        <span className="flex items-center gap-2 text-fg-muted-regular">
          <InlineSpinner size={14} /> Loading billing details…
        </span>
      ) : (
        <div className="flex flex-col gap-2">
          {endsOn !== null && (
            <Callout variant="info" title={`Billing ends on ${endsOn}`}>
              Nothing more will be invoiced, so these details no longer change
              anything. Keep the subscription to edit them again.
            </Callout>
          )}
          {cardOnFile !== null && (
            <div className="flex flex-col">
              <SectionRule label="Payment" sticky />
              <span className="flex items-center gap-3">
                <CreditCardOutlinedIcon
                  aria-hidden
                  sx={{ fontSize: 18 }}
                  className={cardOnFile ? "text-fg-muted" : "text-warning"}
                />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span
                    className={cardOnFile ? "text-fg-regular" : "text-warning"}
                    data-testid="billing-details-card"
                  >
                    {cardName}
                  </span>
                  {!cardOnFile && (
                    <span className="text-fg-muted-regular-small">
                      The next payment will fail until one is added.
                    </span>
                  )}
                  {cardOnFile && card?.expires && (
                    <span className="text-fg-muted-regular-small">
                      Expires {card.expires}
                    </span>
                  )}
                  {cardOnFile && paidBy && (
                    <span
                      className="text-fg-muted-regular-small"
                      data-testid="billing-details-paid-by"
                    >
                      Paid by{" "}
                      <Tooltip title={paidBy.email}>
                        <span className="cursor-help underline decoration-dotted underline-offset-2">
                          {paidBy.is_you ? "you" : paidBy.name}
                        </span>
                      </Tooltip>
                    </span>
                  )}
                </span>
                {onUpdateCard && (
                  <Button
                    variant={cardOnFile ? "secondary" : "primary"}
                    size="sm"
                    className="shrink-0"
                    onClick={onUpdateCard}
                    data-testid="billing-details-update-card"
                  >
                    {cardOnFile ? "Update card" : "Add a card"}
                  </Button>
                )}
              </span>
            </div>
          )}
          <div className="flex flex-col">
            <SectionRule label="Invoices" sticky />
            <div className="flex flex-col gap-2">
              {field("email", "Billing email", "email", {
                info: "Where invoices and receipts are sent.",
                type: "email",
                placeholder: "billing@yourcompany.com",
                disabled: endsOn !== null,
              })}
              {addressTaken && (
                <Callout
                  variant="warning"
                  className="text-fg-regular-small"
                  action={
                    <Button
                      variant="quiet-link"
                      size="xs"
                      onClick={addTypedAddressAsRecipient}
                      data-testid="billing-email-as-recipient"
                    >
                      Add as recipient
                    </Button>
                  }
                >
                  <span data-testid="billing-email-hint">
                    Already used for billing elsewhere. Each organization needs
                    an address of its own.
                  </span>
                </Callout>
              )}
              {!addressTaken && emailHint && (
                <span
                  className="text-fg-muted-regular-small"
                  data-testid="billing-email-hint"
                >
                  {emailHint}
                </span>
              )}
              {field("name", "Name on invoice", "name")}
            </div>
          </div>

          <div className="flex flex-col">
            <SectionRule
              sticky
              label="Company"
              info="Puts a company name and tax number on the invoice instead of your own name, and holds anyone else who should be sent a copy. Leave it off if you are buying as a person and nobody else needs one."
              action={{
                label: showCompany ? "Hide" : hasBusiness ? "Show" : "Add",
                onClick: () => setCompanyAsked(!showCompany),
                testId: "toggle-company-details",
              }}
            />
            <Collapsible open={showCompany} className="flex flex-col gap-2">
              {field("company", "Company name", "company", {
                error: companyMissing
                  ? "Needed here: the tax number and anyone else sent a copy are kept on the company."
                  : undefined,
              })}
              <div className="grid grid-cols-2 gap-2">
                {field("tax", "Tax / VAT number", "tax_identifier", {
                  helper: "VAT comes off the next charge once verified.",
                  error: fieldError("tax_identifier"),
                })}
                {field("company-number", "Company number", "company_number")}
              </div>
              {field("recipients", "Also send invoices to", "recipients", {
                helper: "One email per line.",
                multiline: true,
              })}
            </Collapsible>
          </div>

          {details && (
            <div className="flex flex-col">
              <SectionRule
                sticky
                label="Address"
                info="Where this organization is invoiced. The country decides the tax on it, so keep it current if you move."
                action={{
                  label: showAddress ? "Hide" : hasAddress ? "Show" : "Add",
                  onClick: () => setAddressAsked(!showAddress),
                  testId: "toggle-address-details",
                }}
              />
              <Collapsible open={showAddress} className="flex flex-col gap-2">
                {field("line1", "Address", "first_line")}
                <TextInput
                  value={draft.second_line}
                  onChange={set("second_line")}
                  ariaLabel="Address line 2"
                  disabled={!editable}
                />
                <div className="grid grid-cols-2 gap-2">
                  {field("city", "City", "city")}
                  {field("postal", "Postal code", "postal_code", {
                    error: fieldError("postal_code"),
                  })}
                  {field("region", "Region / state", "region")}
                  <FormField
                    label="Country"
                    htmlFor="billing-country"
                    error={
                      countryMissing
                        ? "Needed: it decides the tax on the invoice."
                        : undefined
                    }
                  >
                    <Select
                      name="billing-country"
                      ariaLabel="Country"
                      value={draft.country_code}
                      onChange={set("country_code")}
                      options={COUNTRIES}
                      disabled={!editable}
                      fullWidth
                    />
                  </FormField>
                </div>
                {countryChanged && (
                  <Callout variant="warning" title="Tax may change">
                    Tax is based on the country and applies from the next
                    invoice.
                  </Callout>
                )}
              </Collapsible>
            </div>
          )}
          {onOpenPortal && (
            <span className="flex">
              <TooltipButton
                title={`Everything billed to ${email}, across every organization you pay for. Opens Paddle, who handles our payments.`}
                variant="quiet-link"
                size="sm"
                icon={<OpenInNewOutlinedIcon className="icon-sm" />}
                onClick={onOpenPortal}
                data-testid="billing-details-portal"
              >
                Receipts and payment methods
              </TooltipButton>
            </span>
          )}
          {error && !claimedByField && (
            <span className="text-error" data-testid="billing-details-error">
              {error}
            </span>
          )}
        </div>
      )}
    </Dialog>
  );
};
